import { goToLink } from "@actions/common";
import { showAlert as showAlertAction } from "@components/alert/action";
import playIcon from "@layouts/assets/images/play_icon.svg";
import DefaultCasinoIcon from "@pages/casino/assets/backgrounds/default-casino-icon.png";
import { openGame } from "@pages/casino/utils";
import {
	CardGameName,
	CardHoverOverlay,
	CardImage,
	CardPlayIcon,
} from "@pages/home/betto/casinoSlider/elements";
import { getBettoDefaultGameIcon, getBettoImageLoadingProps } from "@pages/home/betto/utils";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";

const getImageUrl = (url) => (typeof url === "string" && url.trim() ? url.trim() : null);

const getDefaultGameIcon = () =>
	window.TENANT === "betto" ? getBettoDefaultGameIcon() : DefaultCasinoIcon;

export const hasBettoIconHigh = (game) => Boolean(getImageUrl(game?.icon_high));

export const BETTO_SLIDER_ASPECT_RATIO = "4 / 5";

export const getBettoGameCardAspectRatio = (game) =>
	hasBettoIconHigh(game) ? BETTO_SLIDER_ASPECT_RATIO : "1 / 1";

export const getBettoSliderRowAspectRatio = () => BETTO_SLIDER_ASPECT_RATIO;

const getImageSrc = (game) => {
	const iconHigh = getImageUrl(game?.icon_high);
	const iconCustomUrl = getImageUrl(game?.icon_custom_url);
	const gameIconUrl = getImageUrl(game?.game_icon_url);

	if (iconHigh) return iconHigh;
	if (iconCustomUrl) return iconCustomUrl;
	if (gameIconUrl) return gameIconUrl;
	return getDefaultGameIcon();
};

export { getImageSrc };

const handleBettoGameImageError = (event, game) => {
	const image = event.currentTarget;
	const iconHigh = getImageUrl(game?.icon_high);
	const iconCustomUrl = getImageUrl(game?.icon_custom_url);
	const gameIconUrl = getImageUrl(game?.game_icon_url);

	if (iconHigh && image.dataset.fallback !== "high") {
		image.dataset.fallback = "high";
		image.src = iconHigh;
		return;
	}

	if (iconCustomUrl && image.dataset.fallback !== "custom") {
		image.dataset.fallback = "custom";
		image.src = iconCustomUrl;
		return;
	}

	if (gameIconUrl && image.dataset.fallback !== "game") {
		image.dataset.fallback = "game";
		image.src = gameIconUrl;
		return;
	}

	image.onerror = null;
	image.src = getDefaultGameIcon();
};

export { handleBettoGameImageError };

const BettoGameCard = ({ game, type, isGamePage, imageIndex }) => {
	const dispatch = useDispatch();
	const imageLoadingProps = getBettoImageLoadingProps(imageIndex);

	const handleClick = useCallback(() => {
		const redirect = (...args) => dispatch(goToLink(...args));
		const showAlert = (...args) => dispatch(showAlertAction(...args));

		if (isGamePage) {
			openGame(game, redirect, showAlert, {}, type);
		} else {
			const { pathname, search } = window.location;
			localStorage.setItem("casino.basePath", `${pathname}${search}`);
			openGame(game, redirect, showAlert, {}, type);
		}

		window?.dataLayer?.push({
			event: "casino-game-card-click",
			gameName: game?.remote_name,
			gameId: game?.game_id,
			gameAffiliate: game?.affiliate,
			gameProvider: game?.provider,
			is_live: game?.is_live,
		});
	}, [game, type, isGamePage, dispatch]);

	const handleImageError = useCallback(
		(event) => {
			handleBettoGameImageError(event, game);
		},
		[game]
	);

	return (
		<>
			<CardImage
				data-testid={`button-game-card-${game?.remote_name}`}
				src={getImageSrc(game)}
				alt={game?.remote_name || ""}
				{...imageLoadingProps}
				onClick={handleClick}
				onError={handleImageError}
			/>
			<CardHoverOverlay onClick={handleClick}>
				<CardGameName>{game?.remote_name}</CardGameName>
				<CardPlayIcon
					src={playIcon}
					alt=""
				/>
			</CardHoverOverlay>
		</>
	);
};

export default BettoGameCard;
